import { ComponentType } from '@angular/cdk/portal';
import { KeyValue } from '@angular/common';
import { Injectable } from '@angular/core';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogConfig as MatDialogConfig,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { Observable } from 'rxjs';
import { ConfirmModalComponent } from '../components/confirm-modal/confirm-modal.component';
import { NotificationModalComponent } from '../components/notification-modal/notification-modal.component';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  constructor(private dialog: MatDialog) {}

  confirmDialog(title: string, text: string, callback: Function) {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: title,
        text: text,
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => callback(result));
  }

  notificationDialog(title: string, text: string, okLabel = 'Ok'): void {
    this.openComponentInModal(
      NotificationModalComponent,
      [
        { key: 'title', value: title },
        { key: 'text', value: text },
        { key: 'okLabel', value: okLabel },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.close,
        () => {}
      )
    );
  }

  openComponentInModal<ComponentT, ObservableTypeT>(
    componentRef: ComponentType<ComponentT>,
    componentInputs: KeyValue<string, unknown>[],
    beforeCloseHandlerPair?: ObservableHandlerPair<ComponentT, ObservableTypeT>,
    matDialogConfig: MatDialogConfig = {},
    closeAfterHandle: boolean = true
  ): void {
    const modalRef = this.dialog.open(componentRef, matDialogConfig);
    this.assignComponentInputs(modalRef, componentInputs);

    if (beforeCloseHandlerPair) {
      this.setupModalResultHandler(
        modalRef,
        beforeCloseHandlerPair,
        closeAfterHandle
      );
    }
  }

  private setupModalResultHandler<ComponentT, ObservableTypeT>(
    modalRef: MatDialogRef<ComponentT, any>,
    beforeCloseHandlerPair: ObservableHandlerPair<ComponentT, ObservableTypeT>,
    closeAfterHandle: boolean = true
  ): void {
    //Get the observable, then subscribe for it to be handled. Close modal after handling
    const observable: Observable<ObservableTypeT> =
      beforeCloseHandlerPair.observableGetter(modalRef);
    const handlerSubscription = observable.subscribe((res: ObservableTypeT) => {
      beforeCloseHandlerPair.observableHandler(res);
      if (closeAfterHandle) {
        modalRef.close();
      }
    });
    //Ensure that when modal is closed, the subscription above is also cleaned and no longer using resources
    modalRef
      .afterClosed()
      .toPromise()
      .then(() => {
        if (handlerSubscription != null && !handlerSubscription.closed) {
          handlerSubscription.unsubscribe();
        }
      });
  }

  private assignComponentInputs(
    modalRef: MatDialogRef<unknown, unknown>,
    componentInputs: KeyValue<string, unknown>[]
  ): void {
    componentInputs.forEach((kv) => {
      modalRef.componentInstance[kv.key] = kv.value;
    });
  }
}

export class ObservableHandlerPair<ComponentT, ObservableTypeT> {
  observableGetter: (
    matDialog: MatDialogRef<ComponentT, unknown>
  ) => Observable<ObservableTypeT>;
  observableHandler: (observableType: ObservableTypeT) => void;

  constructor(
    observableGetter: (
      matDialog: MatDialogRef<ComponentT, unknown>
    ) => Observable<ObservableTypeT>,
    observableHandler: (observableType: ObservableTypeT) => void
  ) {
    this.observableGetter = observableGetter;
    this.observableHandler = observableHandler;
  }
}
